import { useMutation } from '@apollo/client';
import { useCallback } from 'react';
import { COUNTRIES } from '@moda/portal-stanchions';
import { PaymentSource } from '../../../constants';
import { BillingAddress } from '../usePaymentMethodsQuery';
import { UPDATE_PAYMENT_METHOD_MUTATION } from './updatePaymentMethodMutation.rest';

type CardArgs = {
  cardholderName: string;
  nonce: string;
};

type UpdateArgs = {
  id: string;
  billingAddress: BillingAddress;
} & CardArgs;

export const useUpdatePaymentMethodMutation = () => {
  const [mutate, { loading, error }] = useMutation(UPDATE_PAYMENT_METHOD_MUTATION);

  const handleUpdate = useCallback(
    async ({ id, billingAddress, ...card }: UpdateArgs) => {
      const country = COUNTRIES.find(country => country.alpha2Code === billingAddress.countryCode);
      const encryptedCard = card.nonce;

      if (!country) throw new Error('updatePaymentMethodMutation: country not found');

      const result = await mutate({
        variables: {
          id,
          input: {
            data: {
              type: 'payments',
              attributes: {
                source: PaymentSource.Braintree,
                cardholder_name: card.cardholderName,
                encrypted_data: encryptedCard,
                billing_address: {
                  id: billingAddress.id,
                  country_name: country.name,
                  extended_address: billingAddress.extendedAddress,
                  locality: billingAddress.locality,
                  postal_code: billingAddress.postalCode,
                  region: billingAddress.region,
                  street_address: billingAddress.streetAddress
                }
              }
            }
          }
        }
      });

      return result;
    },
    [mutate]
  );

  return [handleUpdate, { loading, error }] as const;
};
